import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from 'vue';
import { computed, inject, provide, toValue } from 'vue';

/**
 * @internal
 */
const fieldNullableContextKey = Symbol('fieldNullableContextKey') as InjectionKey<MaybeRefOrGetter<boolean>>;

/**
 * Provide whether the surrounding form field accepts `null`, i.e. can be cleared
 * to empty. Input blocks read this to decide whether to expose a clear affordance.
 */
export function provideFieldNullable(nullable: MaybeRefOrGetter<boolean>) {
	provide(fieldNullableContextKey, nullable);
}

/**
 * Resolve whether a field accepts `null` (i.e. can be cleared to empty).
 *
 * A resolved field's own `isNullable` wins; otherwise it falls back to the
 * ambient form-schema nullability, which defaults to `false`. This is the single
 * source of truth every clear affordance derives from.
 */
export function useFieldNullable(field?: MaybeRefOrGetter<{ isNullable?: boolean } | null | undefined>): ComputedRef<boolean> {
	const injected = inject(fieldNullableContextKey, null);
	return computed(() => toValue(field)?.isNullable ?? toValue(injected) ?? false);
}
